import { useMemo, useState } from "react";

import { CheckCircle2, Clock, FileText } from "lucide-react";

import { SampleDataOverlay } from "./DiscoveryHubSampleState";
import LeaderboardPeriodSelector from "./LeaderboardPeriodSelector";
import { SAMPLE_LEADERBOARD_ROWS } from "./leaderboardSampleData";
import LeaderboardTable from "./LeaderboardTable";
import {
  sortLeaderboardRows,
  toggleLeaderboardSort,
  type LeaderboardRow,
  type LeaderboardSortState,
} from "./leaderboardTableUtils";
import LeaderboardTierKpiCards from "./LeaderboardTierKpiCards";
import {
  DH_PUBLISHED_LEADERBOARD,
  LB_FILTER_LBL,
  LB_FILTERS,
  LB_PANEL,
  LB_STATE_META_LINE,
  LB_STATE_META_STACKED,
  LB_STATE_TITLE,
  lbStateBarClass,
} from "../../Utils/Helper/discoveryHubUi";
import {
  LEADERBOARD_TIER_ORDER,
  SPONSOR_ENGAGEMENT_THEATERS,
  buildLeaderboardQuarterOptions,
  formatQuarterPeriodLabel,
  leaderboardStateTitle,
  rowMatchesTheaterFilter,
} from "../../Utils/Helper/sponsorEngagementUi";

export type LeaderboardSnapshotPayload = {
  period?: string;
  current_period?: string;
  cycle_id?: string;
  publication_state?: string | null;
  published_at?: string | null;
  reviewed_by_name?: string | null;
  available_periods?: string[];
  rows?: LeaderboardRow[];
};

type FilterState = {
  theater: string;
  region: string;
  tier: string;
  search: string;
};

type LeaderboardSnapshotViewProps = {
  data: LeaderboardSnapshotPayload | null;
  loading?: boolean;
  error?: string | null;
  showDescription?: boolean;
  showStateBar?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: LeaderboardRow) => void;
  periodOptions?: string[];
  selectedPeriod?: string;
  onPeriodChange?: (period: string) => void;
};

const LeaderboardSnapshotView = ({
  data,
  loading = false,
  error = null,
  showDescription = true,
  showStateBar = true,
  emptyMessage = "No published leaderboard for this quarter yet.",
  onRowClick,
  periodOptions,
  selectedPeriod,
  onPeriodChange,
}: LeaderboardSnapshotViewProps) => {
  const [sort, setSort] = useState<LeaderboardSortState>({
    col: "rank",
    dir: "asc",
  });
  const [filters, setFilters] = useState<FilterState>({
    theater: "All",
    region: "All",
    tier: "All",
    search: "",
  });

  const rows = data?.rows ?? [];
  const period = selectedPeriod || data?.period || "—";
  const pubState = data?.publication_state ?? "published";
  const hasPublishedRows = pubState === "published" && rows.length > 0;
  const quarterOptions = useMemo(
    () =>
      buildLeaderboardQuarterOptions(
        selectedPeriod,
        data?.period,
        data?.current_period,
        data?.available_periods,
        periodOptions
      ),
    [
      selectedPeriod,
      data?.period,
      data?.current_period,
      data?.available_periods,
      periodOptions,
    ]
  );
  const hasPublishedHistory = (data?.available_periods?.length ?? 0) > 0;
  const isOrgSample = !loading && rows.length === 0 && !hasPublishedHistory;
  const isQuarterPreview =
    !loading &&
    rows.length === 0 &&
    hasPublishedHistory &&
    pubState !== "pending_approval" &&
    pubState !== "draft";
  const showSamplePreview = isOrgSample || isQuarterPreview;
  const displayRows = showSamplePreview ? SAMPLE_LEADERBOARD_ROWS : rows;
  const sampleOverlayMessage = isQuarterPreview
    ? `${formatQuarterPeriodLabel(period)} isn't published yet — preview below, or choose another quarter.`
    : "Preview of the sponsor engagement leaderboard — real rankings appear once a poll cycle is published.";

  const regionOptions = useMemo(() => {
    const regions = new Set<string>();
    displayRows.forEach(r => {
      if (r.region) regions.add(r.region);
    });
    return ["All", ...Array.from(regions).sort()];
  }, [displayRows]);

  const filteredRows = useMemo(() => {
    const result = displayRows.filter(r => {
      if (!rowMatchesTheaterFilter(r.theater, filters.theater)) return false;
      if (filters.region !== "All" && r.region !== filters.region) return false;
      if (filters.tier !== "All" && r.tier !== filters.tier) return false;
      if (
        filters.search &&
        !(r.sponsor_name || r.name || "")
          .toLowerCase()
          .includes(filters.search.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
    return sortLeaderboardRows(result, sort);
  }, [displayRows, filters, sort]);

  const tableEmptyMessage =
    rows.length === 0 ? emptyMessage : "No sponsors match the current filters.";

  const stateIcon =
    pubState === "published" ? (
      <CheckCircle2 size={18} className="shrink-0" />
    ) : pubState === "pending_approval" ? (
      <Clock size={18} className="shrink-0" />
    ) : (
      <FileText size={18} className="shrink-0" />
    );

  return (
    <div className={`${DH_PUBLISHED_LEADERBOARD} ${LB_PANEL}`}>
      {showDescription ? (
        <p className="text-sm text-gray-600 font-normal mb-4 mt-0">
          Official published snapshot · 2-factor composite (Team Feedback,
          Engagement Activity)
        </p>
      ) : null}

      {showStateBar && hasPublishedRows ? (
        <div className={lbStateBarClass(pubState)}>
          {stateIcon}
          <span className={LB_STATE_TITLE}>
            {leaderboardStateTitle(pubState, period)}
          </span>
          {data?.published_at && pubState === "published" ? (
            <div className={LB_STATE_META_STACKED}>
              <span className={LB_STATE_META_LINE}>
                Published on {new Date(data.published_at).toLocaleDateString()}
              </span>
              <span className={LB_STATE_META_LINE}>
                Approved by {data.reviewed_by_name?.trim() || "—"}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={LB_FILTERS}>
        <span className={LB_FILTER_LBL}>Filter</span>
        <select
          value={filters.theater}
          onChange={e => setFilters(f => ({ ...f, theater: e.target.value }))}
        >
          <option value="All">All theaters</option>
          {SPONSOR_ENGAGEMENT_THEATERS.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={filters.region}
          onChange={e => setFilters(f => ({ ...f, region: e.target.value }))}
        >
          {regionOptions.map(r => (
            <option key={r} value={r}>
              {r === "All" ? "All regions" : r}
            </option>
          ))}
        </select>
        <select
          value={filters.tier}
          onChange={e => setFilters(f => ({ ...f, tier: e.target.value }))}
        >
          <option value="All">All tiers</option>
          {LEADERBOARD_TIER_ORDER.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <input
          placeholder="Search sponsor…"
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
        />
        {quarterOptions.length > 0 ? (
          <LeaderboardPeriodSelector
            options={quarterOptions}
            value={selectedPeriod || data?.period}
            onChange={onPeriodChange}
          />
        ) : null}
      </div>

      <SampleDataOverlay
        active={showSamplePreview}
        message={sampleOverlayMessage}
      >
        <LeaderboardTierKpiCards rows={displayRows} />

        <LeaderboardTable
          rows={showSamplePreview ? displayRows : filteredRows}
          sortable
          sort={sort}
          loading={loading}
          onSortToggle={col =>
            setSort(prev => toggleLeaderboardSort(prev, col))
          }
          onRowClick={showSamplePreview ? undefined : onRowClick}
          emptyMessage={error || tableEmptyMessage}
        />
      </SampleDataOverlay>
    </div>
  );
};

export default LeaderboardSnapshotView;
